import JSZip from 'jszip';
import { saveAs } from 'file-saver';

/**
 * ZIP 파일로 내보내기
 * @param {Array} rows - 내보낼 데이터 배열
 * @param {string} fileName - 파일명
 * @param {Function} groupBy - 그룹화 함수 (선택사항, 폴더 구조 생성용) - 그룹 키 반환
 * @param {Function} assets - 각 row에서 파일 목록 추출 함수 (row) => Array<{name: string, url: string}>
 * @param {Function} urlTextBuilder - URL 텍스트 파일 생성 함수 (선택사항, groupBy와 함께 사용)
 * @param {Function} folderNameBuilder - 폴더명 생성 함수 (선택사항, groupKey와 groupRows를 받아 폴더명 반환)
 */
export async function exportToZip(rows, fileName, groupBy = null, assets = null, urlTextBuilder = null, folderNameBuilder = null) {
  if (!rows || rows.length === 0) {
    throw new Error('다운로드할 데이터가 없습니다.');
  }

  if (!assets) {
    throw new Error('assets 함수가 필요합니다.');
  }

  try {
    const zip = new JSZip();

    if (groupBy) {
      // groupBy로 폴더 구조 생성
      const grouped = rows.reduce((groups, row) => {
        const key = groupBy(row);
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(row);
        return groups;
      }, {});

      // 각 그룹별로 폴더 생성 및 파일 추가
      for (const [groupKey, groupRows] of Object.entries(grouped)) {
        // folderNameBuilder가 있으면 사용, 없으면 groupKey 사용
        const folderName = folderNameBuilder 
          ? folderNameBuilder(groupKey, groupRows)
          : groupKey;
        const folder = zip.folder(folderName);

        // URL 텍스트 파일 생성 (urlTextBuilder가 있으면)
        if (urlTextBuilder) {
          const urlContent = groupRows
            .map(row => urlTextBuilder(row))
            .filter(url => url)
            .join('\n');
          if (urlContent) {
            folder.file('url.txt', urlContent);
          }
        }

        // 각 row의 assets 추가
        const addedFiles = new Set(); // 중복 파일 방지
        for (const row of groupRows) {
          const fileList = assets(row);
          if (fileList && fileList.length > 0) {
            for (const file of fileList) {
              // 같은 이름의 파일이 이미 추가되었으면 건너뛰기
              if (addedFiles.has(file.name)) {
                continue;
              }
              
              if (file.url) {
                try {
                  const response = await fetch(file.url);
                  if (response.ok) {
                    const blob = await response.blob();
                    folder.file(file.name, blob);
                    addedFiles.add(file.name);
                  } else {
                    console.warn(`파일 다운로드 실패: ${file.url} (${response.status})`);
                  }
                } catch (error) {
                  console.error(`파일 다운로드 오류 (${file.url}):`, error);
                  // 계속 진행
                }
              }
            }
          }
        }
      }
    } else {
      // groupBy가 없으면 루트에 모든 파일 추가
      for (const row of rows) {
        const fileList = assets(row);
        if (fileList && fileList.length > 0) {
          for (const file of fileList) {
            if (file.url) {
              try {
                const response = await fetch(file.url);
                if (response.ok) {
                  const blob = await response.blob();
                  zip.file(file.name, blob);
                } else {
                  console.warn(`파일 다운로드 실패: ${file.url} (${response.status})`);
                }
              } catch (error) {
                console.error(`파일 다운로드 오류 (${file.url}):`, error);
                // 계속 진행
              }
            }
          }
        }
      }
    }

    // ZIP 파일 생성 및 다운로드
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, fileName);
  } catch (error) {
    console.error('ZIP 내보내기 오류:', error);
    throw new Error(`ZIP 파일 생성 중 오류가 발생했습니다: ${error.message}`);
  }
}

